/**
 * HospitalSetupPasswordPage
 * --------------------------
 * Landing page for the welcome-email one-time setup link. URL pattern:
 *   /hospital/setup-password?token=<token>
 *
 * Flow:
 *   1. On mount, GET /api/hospitals/setup-password/verify?token=…
 *      → renders the user's email + hospital name (or an "expired" error).
 *   2. User picks a password + confirms.
 *   3. POST /api/hospitals/setup-password → backend stores hash, mints JWT,
 *      returns it; we stash it in localStorage under "hospital_token" (same
 *      key used by HospitalLoginPage) and redirect to /hospital/portal.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HospitalSetupPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [verifying, setVerifying] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [verifyError, setVerifyError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token) {
      setVerifyError("Setup link is missing its token. Check the URL in your welcome email.");
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/hospitals/setup-password/verify`, { params: { token } });
        if (!alive) return;
        setTokenInfo(r.data);
      } catch (err) {
        if (!alive) return;
        setVerifyError(err?.response?.data?.detail || "Setup link is invalid or expired.");
      } finally {
        if (alive) setVerifying(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/hospitals/setup-password`, {
        token,
        new_password: password,
        confirm_password: confirm,
      });
      // Persist the session — same key used by HospitalLoginPage's post-login flow
      localStorage.setItem("hospital_token", r.data.token);
      if (r.data.user) localStorage.setItem("hospital_user", JSON.stringify(r.data.user));
      if (r.data.health_system) localStorage.setItem("hospital_health_system", JSON.stringify(r.data.health_system));
      toast.success("Password set — welcome aboard!");
      navigate("/hospital/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Shared dark gradient wrapper to keep visual identity with /hospital/self-register
  const Wrapper = ({ children }) => (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 text-slate-100"
      style={{
        backgroundImage:
          "radial-gradient(1100px 600px at 0% 0%, rgba(14,165,233,0.25), transparent 60%)," +
          "radial-gradient(900px 600px at 100% 80%, rgba(37,99,235,0.20), transparent 55%)," +
          "linear-gradient(180deg, #0a1733 0%, #0b2148 50%, #0a1f44 100%)",
      }}
      data-testid="hospital-setup-password-page"
    >
      {children}
    </div>
  );

  if (verifying) {
    return (
      <Wrapper>
        <div className="bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-2xl p-8 max-w-sm w-full text-center">
          <Loader2 className="w-7 h-7 text-cyan-300 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-300">Verifying your setup link…</p>
        </div>
      </Wrapper>
    );
  }

  if (verifyError) {
    return (
      <Wrapper>
        <div className="bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-2xl p-8 max-w-md w-full text-center" data-testid="setup-link-error">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-300" />
          </div>
          <h1 className="text-xl font-extrabold text-white mb-2">Setup link unavailable</h1>
          <p className="text-sm text-slate-300 mb-5 leading-relaxed">{verifyError}</p>
          <div className="flex gap-2 justify-center">
            <Button asChild variant="outline" className="border-white/20 bg-white/5 text-slate-200 hover:bg-white/10">
              <Link to="/hospital-login">Try logging in</Link>
            </Button>
            <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              <a href="mailto:info@sterilemate.com">Contact Support</a>
            </Button>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-2xl p-8 max-w-md w-full" data-testid="setup-password-card">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 ring-1 ring-emerald-400/30 px-3 py-1.5 rounded-full mb-4">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-200">
              Final step
            </span>
          </div>
          <h1 className="text-3xl font-extrabold text-white leading-tight">Set your password</h1>
          <p className="text-sm text-slate-300 mt-2">
            One last thing before <strong className="text-white">{tokenInfo?.hospital_name || "your hospital"}</strong>
            {" "}can start ordering on Sterile Mate.
          </p>
        </div>

        <div className="rounded-lg bg-white/[0.04] ring-1 ring-white/10 p-3 mb-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Account email</p>
          <p className="text-sm font-bold text-white" data-testid="setup-account-email">{tokenInfo?.email}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs text-slate-300">New Password *</Label>
            <div className="relative mt-1">
              <Input
                type={show ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-emerald-400 pr-10"
                data-testid="setup-new-password"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-white"
                aria-label="Toggle password visibility"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-300">Confirm Password *</Label>
            <Input
              type={show ? "text" : "password"}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-emerald-400"
              data-testid="setup-confirm-password"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting || !password || !confirm}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-12"
            data-testid="setup-submit"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Setting password…</>
              : <><CheckCircle2 className="w-4 h-4 mr-2" />Activate my account <ArrowRight className="w-4 h-4 ml-2" /></>
            }
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            By continuing you agree to use Sterile Mate&apos;s portal for ordering, service calls, and account management.
          </p>
        </form>
      </div>
    </Wrapper>
  );
}
